import { SetStateAction, Dispatch, useEffect, useRef } from "react";
import Spinner from "../spinner/Spinner";
import ModalIcon from "../../assets/img/dashboard/modal-icon.png";
import ProductListStyles from "./ProductListStyles.module.css";

type DeleteProps = {
  setShowDeleteModal: Dispatch<SetStateAction<boolean>>;
  onDeleteSubmitHandler: () => void;
  createProductIsLoading?: boolean;
};

const DeleteProductModal = ({
  setShowDeleteModal,
  onDeleteSubmitHandler,
  createProductIsLoading,
}: DeleteProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Focus trap and initial focus
  useEffect(() => {
    // Focus the confirm button on mount
    confirmButtonRef.current?.focus();

    // Handle Escape key to close modal
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !createProductIsLoading) {
        setShowDeleteModal(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setShowDeleteModal, createProductIsLoading]);

  // Simple focus trap between close and confirm buttons
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      const focusableElements = [closeButtonRef.current, confirmButtonRef.current].filter(Boolean);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  };

  return (
    <div 
      className={ProductListStyles.createProductModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      onKeyDown={handleKeyDown}
    >
      <div className={ProductListStyles.deletePrompt}>
        <div className={ProductListStyles.deletePromptMainContainer}>
          <button
            type="button"
            ref={closeButtonRef}
            className={ProductListStyles.deleteModalCloseButton}
            onClick={(e) => {
              if (createProductIsLoading) {
                e.preventDefault();
                return;
              }
              setShowDeleteModal(false);
            }}
            disabled={createProductIsLoading}
            aria-label="Close dialog"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
          <img
            src={ModalIcon}
            alt=""
            className={ProductListStyles.deleteIcon}
            aria-hidden="true"
          />
          <h1 id="delete-modal-title">Are you sure you want to delete this product?</h1>
          <button 
            type="button"
            ref={confirmButtonRef}
            onClick={onDeleteSubmitHandler}
            disabled={createProductIsLoading}
            aria-busy={createProductIsLoading}
          >
            {createProductIsLoading ? (
              <Spinner variant="small-spinner" alignSpin="center-spinner" />
            ) : (
              "YES"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteProductModal;
